import { Component, LOCALE_ID, Inject, OnInit, OnDestroy, PLATFORM_ID } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { ApiService } from '../../services/api.service';
import { DebtService, DebtPoint } from '../../services/debt.service';
import { Subject, interval, forkJoin } from 'rxjs';
import { takeUntil, switchMap, startWith } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { NgbModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { DebtPopupComponent } from './debt-popup.component';
 

registerLocaleData(localeEs, 'es');

interface Weather {
  temp: number | null;
  description: string;
  icon: string;
}

interface DolarRate {
  casa: string;
  compra: number;
  venta: number;
}

interface Topic {
  section: string;
  tag: string;
  title: string;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterModule, CommonModule, NgbModalModule, DebtPopupComponent],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  currentDate = new Date();
  isMenuOpen = false;
  weather: Weather = {
    temp: null,
    description: '',
    icon: ''
  };
  isLoadingWeather = true;
  isLoadingRates = true;
  isLoadingDebt = true;
  dolarRates: { [key: string]: DolarRate } = {};
  debtLatest: number | null = null;
  debtLatestDate: string | null = null;
  // Fuente dinámica priorizada (INDEC > FRED)
  debtSourceValue: number | null = null;
  debtSourceDate: string | null = null;
  debtSourceLabel: string | null = 'INDEC';
  debtSeries: DebtPoint[] = [];
  debtQuarterlySeries: DebtPoint[] = [];
  debtLastUpdate: Date | null = null;
  hotTopics: Topic[] = [
    {
      title: 'Elecciones 2023',
      section: 'politics',
      tag: 'elecciones'
    },
    {
      title: 'Inflación',
      section: 'economy',
      tag: 'inflacion'
    },
    {
      title: 'Mundial 2026',
      section: 'sports',
      tag: 'mundial'
    },
    {
      title: 'Copa Libertadores',
      section: 'sports',
      tag: 'libertadores'
    },
    {
      title: 'Clima en La Plata',
      section: 'weather',
      tag: 'clima'
    }
  ];

  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private apiService: ApiService,
    private debtService: DebtService,
    private router: Router,
    private modalService: NgbModal,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit() {
    // Carga inicial simple
    this.loadWeather();
    this.loadDolarRates();
    this.loadDebt();
    
    // Iniciar actualizaciones automáticas después de un retraso para no bloquear la carga inicial
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.startAutoRefresh();
      }, 5000); // Retraso de 5 segundos después de la carga inicial

      // Suscribirse a eventos de navegación para recargar datos al navegar
      this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
        if (event.constructor.name === 'NavigationEnd') {
          this.loadWeather();
          this.loadDolarRates();
        }
      });
    }
  }

  private setupAutoRefresh() {
    // Configurar actualización automática del clima cada 10 minutos
    interval(10 * 60 * 1000) // 10 minutos en milisegundos
      .pipe(
        startWith(0), // Ejecutar inmediatamente
        switchMap(() => this.apiService.getWeather()),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (data) => {
          if (data) {
            this.weather = data.current;
          }
          this.isLoadingWeather = false;
        },
        error: () => {
          this.isLoadingWeather = false;
        }
      });

    // Configurar actualización automática del dólar cada 15 minutos
    interval(15 * 60 * 1000) // 15 minutos en milisegundos
      .pipe(
        startWith(0), // Ejecutar inmediatamente
        switchMap(() => this.apiService.getDolarRates()),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (data) => {
          if (Array.isArray(data)) {
            this.dolarRates = data.reduce((acc: { [key: string]: DolarRate }, curr: DolarRate) => {
              acc[curr.casa] = curr;
              return acc;
            }, {});
          }
          this.isLoadingRates = false;
        },
        error: () => {
          this.isLoadingRates = false;
        }
      });
  }

  private loadWeather() {
    this.isLoadingWeather = true;
    this.apiService.getWeather()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          if (data) {
            this.weather = data.current;
          }
          this.isLoadingWeather = false;
        },
        error: () => {
          this.isLoadingWeather = false;
        }
      });
  }

  loadDolarRates() {
    this.isLoadingRates = true;
    this.apiService.getDolarRates().subscribe({
      next: (data) => {
        if (Array.isArray(data)) {
          this.dolarRates = data.reduce((acc: { [key: string]: DolarRate }, curr: DolarRate) => {
            acc[curr.casa] = curr;
            return acc;
          }, {});
        }
        this.isLoadingRates = false;
      },
      error: () => {
        this.isLoadingRates = false;
      }
    });
  }

  loadDebt() {
    this.isLoadingDebt = true;
    this.debtService.getHistoricalDebt().pipe(takeUntil(this.destroy$)).subscribe({
      next: (points) => {
        this.debtSeries = points;
        const last = points.length ? points[points.length - 1] : null;
        this.debtLatest = last ? last.value : null;
        this.debtLatestDate = last ? String(last.date) : null;
        const ts = this.debtService.getLastUpdateTimestamp();
        this.debtLastUpdate = ts ? new Date(ts) : null;
        // Fuente única: INDEC. Usar la última observación; si falla, usar último punto de la serie
        this.debtQuarterlySeries = points;
        this.debtSourceValue = null;
        this.debtSourceDate = null;
        this.debtSourceLabel = 'INDEC';
        this.debtService.getIndecLatestObservation().pipe(takeUntil(this.destroy$)).subscribe({
          next: (indec) => {
            if (indec) {
              this.debtSourceValue = indec.value;
              this.debtSourceDate = indec.date;
            } else if (last) {
              this.debtSourceValue = last.value;
              this.debtSourceDate = String(last.date);
            } else {
              this.debtSourceValue = null;
              this.debtSourceDate = null;
            }
          },
          error: () => {
            if (last) {
              this.debtSourceValue = last.value;
              this.debtSourceDate = String(last.date);
            } else {
              this.debtSourceValue = null;
              this.debtSourceDate = null;
            }
          }
        });
        this.isLoadingDebt = false;
      },
      error: () => {
        this.isLoadingDebt = false;
      }
    });
  }

  // Método manual para forzar actualización (opcional)
  refreshData() {
    // console.log('Actualizando datos manualmente...');
    this.loadWeather();
    this.loadDolarRates();
  }

  // Método para activar actualización automática
  startAutoRefresh() {
    // console.log('Iniciando actualización automática...');
    
    // Variables para almacenar referencias a los intervalos
    let weatherInterval: any;
    let dolarInterval: any;
    let debtInterval: any;
    
    // Actualizar clima cada 10 minutos
    weatherInterval = setInterval(() => {
      // console.log('Actualizando clima automáticamente...');
      this.loadWeather();
    }, 10 * 60 * 1000);

    // Actualizar dólar cada 15 minutos  
    dolarInterval = setInterval(() => {
      // console.log('Actualizando dólar automáticamente...');
      this.loadDolarRates();
    }, 15 * 60 * 1000);

    // Actualizar deuda cada 24 horas (frecuencia baja)
    debtInterval = setInterval(() => {
      this.loadDebt();
    }, 24 * 60 * 60 * 1000);
    
    // Registrar los intervalos para limpiarlos cuando se destruya el componente
    this.destroy$.subscribe(() => {
      if (weatherInterval) clearInterval(weatherInterval);
      if (dolarInterval) clearInterval(dolarInterval);
      if (debtInterval) clearInterval(debtInterval);
    });
  }

  hasDolarRates(): boolean {
    const expectedRates = ['oficial', 'blue', 'turista', 'contadoconliqui', 'bolsa'];
    return !expectedRates.some(rate => !this.dolarRates[rate]);
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
  }

  navigateToSection(section: string, fromMobile: boolean = false) {
    this.router.navigate(['/'], { fragment: section }).then(() => {
      if (isPlatformBrowser(this.platformId)) {
        const element = document.getElementById(section);
        if (element) {
          const headerOffset = 100; // Ajusta este valor según el alto de tu header
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    });
    if (fromMobile) {
      this.toggleMenu();
    }
  }

  navigateToTopic(topic: Topic) {
    // Primero navegamos a la sección
    this.router.navigate(['/'], { fragment: topic.section }).then(() => {
      if (isPlatformBrowser(this.platformId)) {
        const element = document.getElementById(topic.section);
        if (element) {
          const headerOffset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });

          // Emitir evento para filtrar noticias
          this.filterNewsByTag(topic.tag);
        }
      }
    });
    this.toggleMenu();
  }

  private filterNewsByTag(tag: string) {
    if (isPlatformBrowser(this.platformId)) {
      // Emitir un evento personalizado que el home component pueda escuchar
      const event = new CustomEvent('filterNewsByTag', { 
        detail: { tag },
        bubbles: true 
      });
      document.dispatchEvent(event);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Abre el modal con el gráfico de deuda
  openDebtModal() {
    const modalRef = this.modalService.open(DebtPopupComponent, { size: 'lg', centered: true });
    const instance = modalRef.componentInstance as DebtPopupComponent;
    instance.sourceValue = this.debtSourceValue;
    instance.sourceDate = this.debtSourceDate;
    instance.sourceLabel = this.debtSourceLabel;
    instance.quarterlySeries = this.debtQuarterlySeries;
    instance.quarterlySourceLabel = this.debtSourceLabel;
  }
}
